import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/FreeEventRegistrationForm.css";
import { API_BASE_URL } from "../config";

const DEFAULT_FORM_FIELDS = [
  {
    label: "Full Name",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your full name",
    options: [],
  },
  {
    label: "Email Address",
    field_type: "EmailInput",
    is_required: true,
    place_holder: "Enter your email address",
    options: [],
  },
  {
    label: "Phone Number",
    field_type: "PhoneInput",
    place_holder: "98765 43210",
    options: [],
  },
  {
    label: "Organization",
    field_type: "TextInput",
    place_holder: "Enter your organization",
    options: [],
  },
];

/* Pick an icon for a field from its type / label */
const getFieldIcon = ({ label = "", field_type }) => {
  const l = label.toLowerCase();
  if (field_type === "EmailInput" || l.includes("email")) return "fa-regular fa-envelope";
  if (field_type === "PhoneInput" || l.includes("phone")) return "fa-solid fa-phone";
  if (field_type === "DatePicker") return "fa-regular fa-calendar";
  if (field_type === "TimePicker") return "fa-regular fa-clock";
  if (field_type === "Dropdown") return "fa-solid fa-list";
  if (l.includes("access")) return "fa-solid fa-universal-access";
  if (l.includes("diet")) return "fa-regular fa-file-lines";
  if (l.includes("organi") || l.includes("company")) return "fa-regular fa-building";
  if (l.includes("city") || l.includes("location")) return "fa-solid fa-location-dot";
  if (l.includes("name")) return "fa-regular fa-user";
  if (l.includes("hear") || l.includes("source")) return "fa-solid fa-link";
  if (field_type === "TextArea") return "fa-regular fa-file-lines";
  return "fa-solid fa-pen";
};

const FreeEventRegistrationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState(null);
  const [eventError, setEventError] = useState(null);
  const [formType, setFormType] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [terms, setTerms] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationId, setRegistrationId] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);


  const isAttendeeRegistrationOpen = (startDate, endDate) => {
    const now = Date.now();

    if (startDate) {
      const start = new Date(startDate).getTime();
      if (!Number.isNaN(start) && now < start) return false;
    }

    if (endDate) {
      const normalizedEnd = /^\d{4}-\d{2}-\d{2}$/.test(endDate)
        ? `${endDate}T23:59:59.999`
        : endDate;

      const end = new Date(normalizedEnd).getTime();
      if (!Number.isNaN(end) && now > end) return false;
    }

    return true;
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ───────── Load event ───────── */
  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/event/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Event not found");
        return res.json();
      })
      .then((data) => {
        const meta = data?.metadata;
        const cfg =
          meta?.attendees_configuration ||
          meta?.attendeeConfig ||
          data?.attendeeConfig ||
          {};

        setEventData({
          id: meta?.event_id,
          image: data?.logo_file?.base64,
          // Banner image for the header's right side (change to your API's field name)
          banner: data?.banner_file?.base64 || data?.cover_file?.base64 || null,
          name: meta?.name,
          description: meta?.description,
          venue: meta?.venue,
          city: meta?.city,
          address: meta?.address || meta?.venue,
          start_date: meta?.start_date,
          end_date: meta?.end_date,
          attendee_limit: cfg?.default_attendee_limit,
          registration_start: cfg?.start_date,
          registration_end: cfg?.end_date,
        });

        const registrationFormType = cfg?.registration_form_type || "default";

        let customFormFromApi = cfg?.custom_form || [];
        if (typeof customFormFromApi === "string") {
          try {
            customFormFromApi = JSON.parse(customFormFromApi);
          } catch (error) {
            console.error("❌ Failed to parse custom attendee form:", error);
            customFormFromApi = [];
          }
        }

        setFormType(registrationFormType);

        if (
          registrationFormType === "custom" &&
          Array.isArray(customFormFromApi) &&
          customFormFromApi.length > 0
        ) {
          setFormFields(customFormFromApi);
          const initial = {};
          customFormFromApi.forEach((field) => {
            initial[field.label] = field.field_type === "Checkbox" ? false : "";
          });
          setFormValues(initial);
        } else {
          setFormFields(DEFAULT_FORM_FIELDS);
          const initial = {};
          DEFAULT_FORM_FIELDS.forEach((field) => {
            initial[field.label] = "";
          });
          setFormValues(initial);
        }
        setEventError(null);
      })
      .catch((err) => {
        console.error("❌ Failed to load event:", err);
        setEventError("Failed to load event details");
      });
  }, [id]);

  /* ───────── Restore draft ───────── */
  useEffect(() => {
    if (formFields.length === 0) return;
    try {
      const saved = localStorage.getItem(`eventRegistration_${id}`);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.formValues) {
          setFormValues((prev) => ({ ...prev, ...draft.formValues }));
        }
      }
    } catch {
      // ignore
    }
  }, [id, formFields]);

  /* ───────── Auto-save draft ───────── */
  useEffect(() => {
    if (!id || formFields.length === 0) return;
    localStorage.setItem(`eventRegistration_${id}`, JSON.stringify({ formValues }));
  }, [formValues, id, formFields]);

  useEffect(() => {
    validateFormSilently();
  }, [formValues, terms, formFields]);

  /* ───────── Helpers ───────── */
  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;

    if (!end || start.toDateString() === end.toDateString()) {
      return formatDate(startStr);
    }

    return `${formatDate(startStr)} - ${formatDate(endStr)}`;
  };
  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "long" });
  };

  const isRegistrationClosed = (endDate) => {
    if (!endDate) return false;

    // If API returns only YYYY-MM-DD, treat the entire date as open.
    const normalizedDate = /^\d{4}-\d{2}-\d{2}$/.test(endDate)
      ? `${endDate}T23:59:59.999`
      : endDate;

    const endTime = new Date(normalizedDate).getTime();

    return !Number.isNaN(endTime) && Date.now() > endTime;
  };

  const validateFormSilently = () => {
    let isValid = true;

    formFields.forEach((field) => {
      if (field.is_required) {
        const val = formValues[field.label];
        if (field.field_type === "Checkbox") {
          if (!val) isValid = false;
        } else if (val === undefined || val === null || val.toString().trim() === "") {
          isValid = false;
        }
      }

      if (field.field_type === "EmailInput") {
        const val = formValues[field.label];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (val && !emailRegex.test(val)) isValid = false;
        else if (field.is_required && (!val || !emailRegex.test(val))) isValid = false;
      }

      if (field.field_type === "PhoneInput") {
        const val = formValues[field.label];
        const phoneDigits = (val || "").replace(/\D/g, "");
        if (field.is_required && (!phoneDigits || phoneDigits.length < 10)) isValid = false;
      }
    });

    if (!terms) isValid = false;
    setIsFormValid(isValid);
    return isValid;
  };

  const validateAndShowErrors = () => {
    const newErrors = {};
    let isValid = true;

    formFields.forEach((field) => {
      if (field.is_required) {
        const val = formValues[field.label];
        if (field.field_type === "Checkbox") {
          if (!val) {
            newErrors[field.label] = `${field.label} is required`;
            isValid = false;
          }
        } else if (val === undefined || val === null || val.toString().trim() === "") {
          newErrors[field.label] = `${field.label} is required`;
          isValid = false;
        }
      }

      if (field.field_type === "EmailInput") {
        const val = formValues[field.label];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (val && !emailRegex.test(val)) {
          newErrors[field.label] = "Please enter a valid email address";
          isValid = false;
        } else if (field.is_required && (!val || !emailRegex.test(val))) {
          newErrors[field.label] = "Please enter a valid email address";
          isValid = false;
        }
      }

      if (field.field_type === "PhoneInput") {
        const val = formValues[field.label];
        const phoneDigits = (val || "").replace(/\D/g, "");
        if (field.is_required && (!phoneDigits || phoneDigits.length < 10)) {
          newErrors[field.label] = "Please enter a valid 10-digit phone number";
          isValid = false;
        }
      }
    });

    if (!terms) {
      newErrors.terms = "You must agree to the terms to register";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  /* ───────── Submit ───────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateAndShowErrors()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const body = new URLSearchParams();
      body.append("eventId", Number(id));
      Object.entries(formValues).forEach(([key, value]) => {
        body.append(`form[${key}]`, value);
      });

      const response = await fetch(`${API_BASE_URL}/attendee_registration`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await response.json();

      if (!response.ok) {
        const backendMessage =
          data?.Error || data?.error || data?.message || "Registration failed";
        throw new Error(backendMessage);
      }

      const regId =
        data?.registration_id ||
        data?.result?.registration_id ||
        `EF-${id}-${Math.floor(1000 + Math.random() * 9000)}`;

      setRegistrationId(regId);
      setSuccess(true);
      localStorage.removeItem(`eventRegistration_${id}`);
    } catch (error) {
      console.error("❌ Registration error:", error);
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    localStorage.setItem(`eventRegistration_${id}`, JSON.stringify({ formValues }));
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2200);
  };

  const getFieldValue = (labelToFind) => {
    const field = formFields.find(
      (f) =>
        f.label.toLowerCase().includes(labelToFind.toLowerCase()) ||
        f.field_type === labelToFind,
    );
    if (field) return formValues[field.label] || "-";
    return "-";
  };

  const handleFieldChange = (label, value) => {
    setFormValues((prev) => ({ ...prev, [label]: value }));
  };

  /* ───────── Field renderer ───────── */
  const renderField = (field, index) => {
    const { label, field_type, options, is_required, place_holder } = field;
    const value = formValues[label] ?? (field_type === "Checkbox" ? false : "");
    const hasError = showValidation && !!errors[label];
    const fieldId = `efr_field_${index}`;
    const icon = getFieldIcon(field);

    const inputTypeMap = {
      TextInput: "text",
      EmailInput: "email",
      PhoneInput: "tel",
      NumberInput: "number",
      DatePicker: "date",
      TimePicker: "time",
    };

    const labelEl = (
      <label className="efr-label" htmlFor={fieldId}>
        {label} {is_required && <span className="efr-req">*</span>}
      </label>
    );
    const errorEl = hasError && <div className="efr-error">{errors[label]}</div>;
    const invalid = hasError ? " is-invalid" : "";

    switch (field_type) {

      case "PhoneInput":
        return (
          <div className="efr-field" key={index}>
            {labelEl}

            <div className={`efr-phone${invalid}`}>
              <span className="efr-phone__icon">
                <i className="fa-solid fa-phone" />
              </span>

              <input
                id={fieldId}
                type="tel"
                inputMode="numeric"
                className="efr-phone__input"
                placeholder={place_holder || "Enter your phone number"}
                value={value}
                onChange={(e) =>
                  handleFieldChange(
                    label,
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
              />
            </div>

            {errorEl}
          </div>
        );

      case "TextInput":
      case "EmailInput":
      case "NumberInput":
      case "DatePicker":
      case "TimePicker":
        return (
          <div className="efr-field" key={index}>
            {labelEl}
            <div className="efr-control">
              <i className={`efr-control__icon ${icon}`} />
              <input
                id={fieldId}
                type={inputTypeMap[field_type]}
                className={`efr-input${invalid}`}
                placeholder={place_holder || `Enter ${label}`}
                value={value}
                onChange={(e) => handleFieldChange(label, e.target.value)}
              />
            </div>
            {errorEl}
          </div>
        );

      case "TextArea":
        return (
          <div className="efr-field" key={index}>
            {labelEl}
            <div className="efr-control efr-control--top">
              <i className={`efr-control__icon ${icon}`} />
              <textarea
                id={fieldId}
                className={`efr-input efr-textarea${invalid}`}
                rows={2}
                placeholder={place_holder || `Enter ${label}`}
                value={value}
                onChange={(e) => handleFieldChange(label, e.target.value)}
              />
            </div>
            {errorEl}
          </div>
        );

      case "Dropdown":
        return (
          <div className="efr-field" key={index}>
            {labelEl}
            <div className="efr-control">
              <i className={`efr-control__icon ${icon}`} />
              <select
                id={fieldId}
                className={`efr-input efr-select${invalid}`}
                value={value}
                onChange={(e) => handleFieldChange(label, e.target.value)}
              >
                <option value="">Select {label}</option>
                {(options || []).map((opt, i) => (
                  <option key={i} value={typeof opt === "object" ? opt.value : opt}>
                    {typeof opt === "object" ? opt.label : opt}
                  </option>
                ))}
              </select>
            </div>
            {errorEl}
          </div>
        );

      case "RadioGroup":
        return (
          <div className="efr-field efr-field--full" key={index}>
            <span className="efr-label">
              {label} {is_required && <span className="efr-req">*</span>}
            </span>
            <div className="efr-radios">
              {(options || []).map((opt, i) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return (
                  <label key={i} className="efr-radio">
                    <input
                      type="radio"
                      name={fieldId}
                      value={optVal}
                      checked={value === optVal}
                      onChange={(e) => handleFieldChange(label, e.target.value)}
                    />
                    {optLabel}
                  </label>
                );
              })}
            </div>
            {errorEl}
          </div>
        );

      case "Checkbox":
        return (
          <div className="efr-field efr-field--check" key={index}>
            <div className="efr-check">
              <input
                id={fieldId}
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleFieldChange(label, e.target.checked)}
              />
              <label className="efr-check__label" htmlFor={fieldId}>
                {label} {is_required && <span className="efr-req">*</span>}
              </label>
            </div>
            {errorEl}
          </div>
        );

      default:
        return (
          <div className="efr-field" key={index}>
            {labelEl}
            <div className="efr-control">
              <i className={`efr-control__icon ${icon}`} />
              <input
                id={fieldId}
                type="text"
                className={`efr-input${invalid}`}
                placeholder={place_holder || `Enter ${label}`}
                value={value}
                onChange={(e) => handleFieldChange(label, e.target.value)}
              />
            </div>
            {errorEl}
          </div>
        );
    }
  };

  /* ───────── States ───────── */
  if (eventError) {
    return (
      <div className="efr-page">
        <div className="efr-container">
          <button className="efr-back" onClick={() => navigate(-1)}>
            <i className="fa-solid fa-arrow-left"></i> Back
          </button>
          <div className="efr-state efr-state--error">
            <i className="fas fa-exclamation-circle"></i>
            <p>{eventError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!eventData || formType === null) {
    return (
      <div className="efr-page">
        <div className="efr-container">
          <div className="efr-state">
            <div className="efr-spinner" />
            <div>Loading event details...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show closed page when attendee registration deadline has passed
  const registrationOpen = isAttendeeRegistrationOpen(
    eventData?.registration_start,
    eventData?.registration_end
  );

  if (!registrationOpen) {
    return (
      <div className="attendee-closed-page">
        <div className="attendee-closed-content">

          <div className="attendee-closed-icon">
            <i className="fas fa-calendar-xmark"></i>
          </div>

          <span className="attendee-closed-badge">
            <i className="fas fa-lock"></i>
            Attendee Registration Closed
          </span>

          <h1>Attendee Registration is Closed</h1>

          <p className="attendee-closed-subtitle">
            Thank you for your interest in attending{" "}
            <strong>{eventData?.name}</strong>.
          </p>

          <p className="attendee-closed-description">
            The registration deadline has passed or attendee registration
            is currently unavailable for this event.
          </p>

          <div className="attendee-closed-actions">
            <button
              className="attendee-closed-primary"
              onClick={() => navigate(`/eventpage/${id}#info`)}
            >
              <i className="fas fa-calendar-alt"></i>
              Back to Event Details
            </button>

            <button
              className="attendee-closed-secondary"
              onClick={() => navigate("/")}
            >
              <i className="fas fa-home"></i>
              Browse More Events
            </button>
          </div>

        </div>
      </div>
    );
  }

  const venueSub =
    [eventData.address && eventData.address !== eventData.venue ? eventData.address : null, eventData.city]
      .filter(Boolean)
      .join(", ") || "Location TBD";

  /* ───────── Main render ───────── */
  return (
    <div className="efr-page">
      <div className="efr-container">
        <button className="efr-back" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>

        {!success ? (
          <>
            {/* ── Header banner ── */}
            <header className="efr-hero">
              {eventData.banner && (
                <div
                  className="efr-hero__bg"
                  style={{ backgroundImage: `url(${eventData.banner})` }}
                />
              )}
              {/* overlay covers only the left side, behind the text */}
              <div className="efr-hero__overlay" />

              <div className="efr-hero__body">
                <div className="efr-hero__head">
                  {eventData.image && (
                    <img
                      src={eventData.image}
                      alt={eventData.name}
                      className="efr-hero__logo"
                    />
                  )}
                  <h1 className="efr-hero__title">{eventData.name}</h1>
                </div>

                {eventData.description && (
                  <p className="efr-hero__desc" title={eventData.description}>
                    {eventData.description}
                  </p>
                )}

                <div className="efr-hero__meta">
  {/* Registration Deadline */}
  <div className="efr-chip">
    <i className="fas fa-calendar-check efr-chip__icon"></i>
    <div className="efr-chip__text">
      <span className="efr-chip__title">
        Registration Deadline
      </span>
      <span className="efr-chip__sub">
        {formatDateRange(eventData.registration_end, null)}
      </span>
    </div>
  </div>

  {/* Conference Venue */}
  <div className="efr-chip efr-chip--venue">
    <i className="fas fa-map-marker-alt efr-chip__icon"></i>
    <div className="efr-chip__text">
      <span className="efr-chip__title">
        Conference Venue
      </span>
      <span className="efr-chip__sub efr-chip__venue-value">
        {[eventData.venue, eventData.address !== eventData.venue ? eventData.address : null, eventData.city]
          .filter(Boolean)
          .join(", ") || "Location TBD"}
      </span>
    </div>
  </div>
</div>



              </div>
            </header>

            {/* ── Form card ── */}
            <section className="efr-card">
              {loading && (
                <div className="efr-loader">
                  <div className="efr-spinner" />
                </div>
              )}

              <div className="efr-card__head">
                <div className="efr-card__avatar">
                  <i className="fa-regular fa-user"></i>
                </div>
                <div>
                  <h2 className="efr-card__title">Event Registration</h2>
                  <p className="efr-card__sub">
                    Fill in your details below to register for {eventData.name}.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="efr-form" noValidate>
                {formFields.map((field, index) => renderField(field, index))}

                <div className="efr-field efr-field--check">
                  <div className="efr-check">
                    <input
                      id="efr_terms"
                      type="checkbox"
                      checked={terms}
                      onChange={(e) => setTerms(e.target.checked)}
                    />
                    <label className="efr-check__label" htmlFor="efr_terms">
                      I agree to the <a href="/terms">Terms of Service</a> and{" "}
                      <a href="/privacy">Privacy Policy</a>{" "}
                      <span className="efr-req">*</span>
                    </label>
                  </div>
                  {showValidation && errors.terms && (
                    <div className="efr-error">{errors.terms}</div>
                  )}
                </div>

                {showValidation && errors.submit && (
                  <div className="efr-field efr-field--full">
                    <div className="efr-error efr-error--submit">{errors.submit}</div>
                  </div>
                )}

                <div className="efr-actions">
                  <span className={`efr-draft-note${draftSaved ? " is-visible" : ""}`}>
                    <i className="fa-solid fa-check"></i> Draft saved
                  </span>

                  {/* <button
                    type="button"
                    className="efr-btn efr-btn--ghost"
                    onClick={handleSaveDraft}
                    disabled={loading}
                  >
                    <i className="fa-regular fa-file-lines"></i> Save as Draft
                  </button> */}

                  <button
                    type="submit"
                    className={`efr-btn efr-btn--primary${!isFormValid ? " is-off" : ""}`}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Register Now →"}
                  </button>
                </div>
              </form>
            </section>
          </>
        ) : (
          /* ── Success State ── */
          <div className="efr-success">
            <div className="efr-success__icon">
              <i className="fas fa-check" />
            </div>

            <h2 className="efr-success__title">You're registered!</h2>
            <p className="efr-success__sub">
              You're all set for <strong>{eventData.name}</strong>. A confirmation has
              been sent to your email.
            </p>

            <div className="efr-success__details">
              <div className="efr-success__row">
                <span className="efr-success__label">Event</span>
                <span className="efr-success__value">{eventData.name}</span>
              </div>
              <div className="efr-success__row">
                <span className="efr-success__label">Date</span>
                <span className="efr-success__value">
                  {formatDateRange(eventData.start_date, eventData.end_date)}
                </span>
              </div>
              <div className="efr-success__row">
                <span className="efr-success__label">Venue</span>
                <span className="efr-success__value">
                  {[eventData.venue, eventData.city].filter(Boolean).join(", ")}
                </span>
              </div>
              <div className="efr-success__row">
                <span className="efr-success__label">Registration ID</span>
                <span className="efr-success__value">
                  <span className="efr-success__pill">{registrationId}</span>
                </span>
              </div>
              <div className="efr-success__row">
                <span className="efr-success__label">Attendee</span>
                <span className="efr-success__value">
                  {formValues["Full Name"] || getFieldValue("Name") || "—"}
                </span>
              </div>
            </div>

            <p className="efr-success__mail">
              <i className="fas fa-envelope"></i>
              Confirmation sent to{" "}
              <strong>
                {formValues["Email Address"] || getFieldValue("EmailInput") || "—"}
              </strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeEventRegistrationForm;